import { fetchModules, type TAliasModule, type TAdditionalInformation } from '@gialaboratory/sdk/report-check-api';
import React, { useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import InfoCard from '@@components/InfoCard/InfoCard';
import { TLocale } from '@@config/locale';
import { TReportTypeCode } from '@@config/report';
import { LocaleContext, TLocaleContext } from '@@contexts/LocaleContext';
import { getMagnoliaRestBaseURL, hashKey } from '@@utils/utils';

const metaTitleMap = new Map<string, string>([
    ['QAD', '__Quality-Assurance-Services__'],
    ['JG', '__Additional-Info-Diamonds__'],
    ['JGC', '__Additional-Info-Diamonds__'],
]);

const fetchAdditionalInformationData = async (
    locale: TLocale,
    reportTypeCode: TReportTypeCode
): Promise<TAdditionalInformation> => {
    const metaTitle = metaTitleMap.get(reportTypeCode) ?? '__Additional-Info-Diamonds__';
    const baseUrl = getMagnoliaRestBaseURL();
    return fetchModules(metaTitle, locale, { baseUrl });
};

type TAdditionalInformationProps = {
    readonly reportTypeCode: TReportTypeCode;
};

const AdditionalInformation: React.FC<TAdditionalInformationProps> = ({ reportTypeCode }) => {
    const { locale }: TLocaleContext = useContext(LocaleContext);

    const { data, isLoading, error } = useQuery({
        queryKey: ['additionalInformation', locale, reportTypeCode],
        queryFn: () => fetchAdditionalInformationData(locale, reportTypeCode),
        staleTime: 15 * 60 * 1000, // 15 minutes
    });

    if (error) {
        console.error(error);
        return <div className='hidden'>Error loading additional information content</div>;
    }

    return (
        <>
            {!isLoading ? (
                <>
                    {data?.ALIAS_MODULES && (
                        <div className='grid grid-cols-1 sm:grid-cols-3'>
                            {data.ALIAS_MODULES.map((module: TAliasModule) => (
                                <InfoCard data={module} key={hashKey(module.TITLE)} />
                            ))}
                        </div>
                    )}
                </>
            ) : (
                <div className='skeleton' style={{ minHeight: '10rem' }}></div>
            )}
        </>
    );
};

export default AdditionalInformation;
